import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Check, Globe2, FileDown } from "lucide-react";
import SEO, { websiteSchema } from "@/components/SEO";
import Marquee from "@/components/Marquee";
import { KineticLines, Reveal, SectionHead } from "@/components/Motion";
import { CategoryCard, IndustryCard } from "@/components/Cards";
import CTASection from "@/components/CTASection";
import { IMAGES } from "@/config/images";
import useSettings from "@/lib/useSettings";
import api from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

const WHY = [
  ["Precision Manufacturing", "Equipment built to engineering drawings with controlled fabrication processes and dimensional checks at every stage."],
  ["Custom Engineering", "Standard platforms adapted to your dimensions, materials, interlock logic and application requirements."],
  ["Stainless Steel Expertise", "Deep fabrication experience in SS 304 stainless steel with cleanroom-compatible finishes and weld quality."],
  ["Quality-Focused Production", "Material selection, surface finish and functional inspection treated as design inputs, not afterthoughts."],
  ["Application-Driven Design", "Every solution starts from the process — airflow, containment, transfer and cleaning requirements."],
  ["Global Market Orientation", "Engineering, documentation and packaging practices aligned with international B2B and export expectations."],
];

const CAPABILITIES = [
  "CNC / Fibre Laser Cutting", "CNC Press Brake Forming", "Laser Welding", "TIG Welding",
  "Precision Sheet Metal Fabrication", "Stainless Steel Fabrication", "Assembly & Finishing", "Custom Manufacturing",
];

const MARKETS = ["India", "UAE", "Saudi Arabia", "GCC & Middle East", "Africa", "Other International Markets"];

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [industries, setIndustries] = useState([]);
  const settings = useSettings();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/industries").then((r) => setIndustries(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <SEO title={null} path="/" image={IMAGES.hero} jsonLd={websiteSchema} />

      {/* HERO */}
      <section ref={heroRef} className="blueprint-dark relative overflow-hidden bg-ink" data-testid="hero-section">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_70%_10%,rgba(11,107,184,0.16),transparent_55%)]" />
        <div className="relative mx-auto grid max-w-7xl gap-12 px-4 pb-16 pt-16 sm:px-6 lg:grid-cols-12 lg:gap-8 lg:px-8 lg:pb-24 lg:pt-24">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.05 }}
              className="eyebrow eyebrow-light"
            >
              STJ Innovations LLP — New Delhi, India
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.12 }}
              className="mt-4 font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-white/85 sm:text-[13px] sm:tracking-[0.3em]"
            >
              Clean Spaces <span className="text-sky-300">|</span> Better Tomorrow
            </motion.p>
            <KineticLines
              lines={["Engineered for", "Controlled", "Environments."]}
              className="mt-6 font-display text-[42px] font-medium leading-[1.04] tracking-tight text-white sm:text-6xl lg:text-[76px]"
            />
            <motion.p
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.7 }}
              className="mt-6 font-mono text-[12px] uppercase tracking-[0.22em] text-sky-300"
            >
              Pharmaceutical Cleanroom Equipment & Precision Engineering Solutions
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.82 }}
              className="mt-5 max-w-xl text-base leading-relaxed text-slate-400"
            >
              STJ Innovations designs and manufactures cleanroom equipment, pharmaceutical engineering solutions and precision sheet-metal products for controlled environments and demanding industrial applications.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.95 }}
              className="mt-9 flex flex-col gap-3 sm:flex-row"
            >
              <Link to="/products" data-testid="hero-explore-products-button"
                onClick={() => trackEvent("cta_click", { cta: "hero_explore_products" })}
                className="group inline-flex items-center justify-center gap-2 bg-accent px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-accent-dark">
                Explore Products
                <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
              </Link>
              <Link to="/request-a-quote" data-testid="hero-request-quote-button"
                onClick={() => trackEvent("cta_click", { cta: "hero_request_quote" })}
                className="inline-flex items-center justify-center gap-2 border border-white/20 px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:border-white/50">
                Request a Quote
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 1.05 }}
              className="mt-5"
            >
              <a href="/STJ-Innovations-Pharmaceutical-Cleanroom-Equipment-Catalogue.pdf" target="_blank" rel="noopener noreferrer"
                data-testid="hero-catalogue-download"
                onClick={() => trackEvent("datasheet_download", { document: "product_catalogue", from: "home_hero" })}
                className="group inline-flex items-center gap-2 font-mono text-[12px] uppercase tracking-[0.2em] text-sky-300 transition-colors hover:text-white">
                <FileDown size={14} /> Download STJ Product Catalogue
              </a>
              <p className="mt-1.5 text-xs text-slate-500">
                Explore our pharmaceutical cleanroom equipment, controlled-environment solutions and precision engineering products.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.15 }}
              className="mt-12 flex flex-wrap gap-x-8 gap-y-2 border-t border-white/10 pt-5 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500"
            >
              <span>SS 304 Stainless Steel</span><span>HEPA-Filtered Airflow</span><span>Custom Engineering</span><span>Made in India</span>
            </motion.div>
          </div>

          <div className="relative lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="spotlight-img relative overflow-hidden border border-white/10"
            >
              <motion.img
                src={settings.img_home_hero || IMAGES.hero}
                alt="Stainless steel pharmaceutical cleanroom equipment manufactured by STJ Innovations"
                style={{ y: imgY, scale: 1.12 }}
                className="aspect-[4/5] w-full object-cover lg:aspect-auto lg:h-[560px]"
              />
              <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between bg-ink/85 px-4 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400 backdrop-blur">
                <span>Cleanroom Equipment</span><span className="text-sky-300">STJ / IN</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee dark />

      {/* INTRODUCTION */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32" data-testid="intro-section">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHead eyebrow="Who We Are" title="Engineering Precision for Critical Environments" />
            <Reveal delay={0.2}>
              <Link to="/about" data-testid="intro-discover-button"
                className="group mt-8 inline-flex items-center gap-2 border border-ink px-6 py-3 text-sm font-semibold text-ink transition-colors hover:bg-ink hover:text-white">
                Discover STJ Innovations
                <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.15}>
              <p className="text-lg font-light leading-relaxed text-ink sm:text-xl">
                STJ Innovations combines engineering, precision manufacturing and practical application knowledge to develop reliable solutions for pharmaceutical, healthcare, laboratory and controlled-environment applications.
              </p>
              <p className="mt-6 text-base leading-relaxed text-steel">
                From pass boxes and laminar air flow units to cleanroom furniture and precision sheet-metal components, every product is designed around the environment it will work in — and manufactured with the discipline those environments demand.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PRODUCT CATEGORIES */}
      <section className="blueprint-light border-y border-line bg-mist" data-testid="solutions-section">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHead eyebrow="Our Solutions" title="Cleanroom Equipment & Precision Fabrication"
              copy="Nine product families covering material transfer, clean air, cleanroom construction, furniture, containment and smart connected equipment." />
            <Reveal delay={0.2}>
              <Link to="/products" data-testid="solutions-view-all-button"
                className="group inline-flex items-center gap-2 font-mono text-[12px] uppercase tracking-[0.2em] text-accent hover:text-accent-dark">
                View all products <ArrowRight size={13} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {categories.map((c, i) => <CategoryCard key={c.slug} cat={c} index={i} />)}
          </div>
        </div>
      </section>

      {/* WHY STJ */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32" data-testid="why-stj-section">
        <SectionHead eyebrow="Why STJ Innovations" title="Built on Engineering Discipline" />
        <div className="mt-14 grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-3">
          {WHY.map(([title, copy], i) => (
            <Reveal key={title} delay={(i % 3) * 0.07} className="bg-white">
              <div className="h-full p-8" data-testid={`why-item-${i}`}>
                <p className="font-mono text-[11px] tracking-[0.2em] text-accent">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="mt-4 font-display text-xl font-semibold tracking-tight text-ink">{title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-steel">{copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* MANUFACTURING */}
      <section className="blueprint-dark bg-navy" data-testid="manufacturing-section">
        <div className="mx-auto grid max-w-7xl gap-14 px-4 py-24 sm:px-6 lg:grid-cols-12 lg:px-8 lg:py-32">
          <div className="lg:col-span-6">
            <SectionHead dark eyebrow="Manufacturing" title="Built on Modern Manufacturing"
              copy="STJ's fabrication capability spans cutting, forming, welding, finishing and assembly of stainless steel and sheet-metal products. Machine photographs and equipment specifications are published as our facility documentation grows — contact us for current capability details." />
            <Reveal delay={0.2}>
              <ul className="mt-10 grid gap-x-8 gap-y-4 sm:grid-cols-2">
                {CAPABILITIES.map((c, i) => (
                  <li key={c} className="flex items-start gap-3 text-sm text-slate-300" data-testid={`capability-item-${i}`}>
                    <Check size={15} className="mt-0.5 shrink-0 text-sky-400" /> {c}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.28}>
              <Link to="/capabilities" data-testid="manufacturing-explore-button"
                className="group mt-10 inline-flex items-center gap-2 bg-accent px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-accent-dark">
                Explore Our Manufacturing Capabilities
                <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
              </Link>
            </Reveal>
          </div>
          <div className="relative lg:col-span-5 lg:col-start-8">
            <Reveal delay={0.15}>
              <div className="spotlight-img overflow-hidden border border-white/10">
                <img src={settings.img_manufacturing_primary || IMAGES.laser} alt="Fibre laser cutting of stainless steel sheet for cleanroom equipment" loading="lazy"
                  className="aspect-[4/3] w-full object-cover" />
              </div>
              <div className="spotlight-img -mt-10 ml-auto w-3/4 overflow-hidden border border-white/10 shadow-2xl">
                <img src={settings.img_manufacturing_secondary || IMAGES.cnc} alt="CNC precision forming of sheet metal components" loading="lazy"
                  className="aspect-[16/10] w-full object-cover" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32" data-testid="industries-section">
        <SectionHead eyebrow="Industries" title="Solutions Across Critical Industries"
          copy="Equipment and fabrication for environments where contamination control, cleanability and reliability are non-negotiable." />
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {industries.slice(0, 8).map((ind, i) => <IndustryCard key={ind.slug} industry={ind} index={i} />)}
        </div>
      </section>

      {/* QUALITY */}
      <section className="border-y border-line bg-mist" data-testid="quality-section">
        <div className="mx-auto grid max-w-7xl gap-14 px-4 py-24 sm:px-6 lg:grid-cols-12 lg:px-8 lg:py-32">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="spotlight-img overflow-hidden border border-line">
                <img src={settings.img_quality || IMAGES.micro} alt="Surface finish and weld inspection of stainless steel cleanroom equipment" loading="lazy"
                  className="aspect-[4/5] w-full object-cover" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <SectionHead eyebrow="Quality by Design" title="Quality Is Engineered In, Not Inspected On"
              copy="Every STJ product is developed around material selection, fabrication precision, surface finish, dimensional accuracy, functional testing, documentation and the requirements of the application it serves." />
            <Reveal delay={0.2}>
              <ul className="mt-10 grid gap-x-8 gap-y-3.5 sm:grid-cols-2">
                {["Material selection", "Fabrication precision", "Surface finish", "Dimensional accuracy", "Functional testing", "Documentation", "Application requirements"].map((q, i) => (
                  <li key={q} className="flex items-center gap-3 text-sm font-medium text-ink" data-testid={`quality-item-${i}`}>
                    <span className="h-px w-6 bg-accent" /> {q}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.28}>
              <Link to="/quality" data-testid="quality-explore-button"
                className="group mt-10 inline-flex items-center gap-2 border border-ink px-6 py-3 text-sm font-semibold text-ink transition-colors hover:bg-ink hover:text-white">
                Quality & Engineering
                <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* GLOBAL */}
      <section className="blueprint-dark relative overflow-hidden bg-ink" data-testid="global-section">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_20%_80%,rgba(11,107,184,0.12),transparent_50%)]" />
        <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
          <div className="grid items-center gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <SectionHead dark eyebrow="Global Orientation" title="Made in India. Built for Global Markets."
                copy="STJ Innovations is developing its manufacturing and export capabilities to serve customers in India and international markets — with engineering, documentation and communication practices built for international B2B business." />
              <Reveal delay={0.2}>
                <div className="mt-10 flex flex-wrap gap-2.5">
                  {MARKETS.map((m) => (
                    <span key={m} data-testid={`market-chip-${m.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="border border-white/15 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-300">
                      {m}
                    </span>
                  ))}
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.15}>
                <div className="border border-white/10 bg-white/[0.03] p-8">
                  <Globe2 size={28} className="text-sky-400" />
                  <p className="mt-5 font-display text-xl font-medium text-white">International Business Development</p>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">
                    We welcome enquiries from pharmaceutical manufacturers, EPC companies, cleanroom contractors, distributors and OEM customers across India, the Middle East, Africa and other international markets.
                  </p>
                  <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3">
                    <Link to="/markets" data-testid="global-markets-button"
                      className="group inline-flex items-center gap-2 font-mono text-[12px] uppercase tracking-[0.2em] text-sky-300 hover:text-white">
                      Explore global markets <ArrowRight size={13} className="transition-transform group-hover:translate-x-1" />
                    </Link>
                    <Link to="/contact" data-testid="global-contact-button"
                      className="group inline-flex items-center gap-2 font-mono text-[12px] uppercase tracking-[0.2em] text-sky-300 hover:text-white">
                      Start an enquiry <ArrowRight size={13} className="transition-transform group-hover:translate-x-1" />
                    </Link>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
